/**
 * Optional embed configuration when editing website popups (editorType `popup`).
 * Stored under {@link TemplateSettings.popup}.
 */

import type { SpacingValue } from "./blocks";

/** Logical combiner when both delay and page-view rules apply. */
export type PopupTriggerConditionsOperator = "and" | "or";

/**
 * Extra funnel rules before a trigger may fire (delay since load, page view count).
 * Stored on each trigger slot; serialized for embed runtimes via {@link toPopupTriggersEmbedJson}.
 */
export interface PopupTriggerConditions {
  enabled: boolean;
  delaySeconds: number;
  pageViews: number;
  operator: PopupTriggerConditionsOperator;
}

export type PopupSizePreset =
  | "small"
  | "medium"
  | "large"
  | "fullscreen"
  | "fullHeight"
  | "custom";

export interface PopupTriggerSlot {
  enabled: boolean;
  /** Optional funnel conditions (delay, page views). */
  triggerConditions?: PopupTriggerConditions;
  /** On scroll — percent of viewport height */
  scrollPercent?: number;
  /** On click — hash fragment or selector snippet shown to merchant */
  clickAnchor?: string;
  /** On hover — element id */
  hoverElementId?: string;
  /** Custom event name */
  customEventName?: string;
  /** Exit intent — also react when user scrolls up near the top. */
  triggerOnScrollUp?: boolean;
  /** Exit intent — react to back navigation where the runtime supports it. */
  triggerOnBackButton?: boolean;
}

export interface PopupTriggersSettings {
  onLanding: PopupTriggerSlot;
  /** Exit intent — visitor moves to leave the page */
  onExit: PopupTriggerSlot;
  onScroll: PopupTriggerSlot;
  onClick: PopupTriggerSlot;
  onHover: PopupTriggerSlot;
  onCustomEvent: PopupTriggerSlot;
}

/** Anchor point for the popup panel within the viewport. */
export type PopupScreenPosition =
  | "topLeft"
  | "topCenter"
  | "topRight"
  | "middleLeft"
  | "center"
  | "middleRight"
  | "bottomLeft"
  | "bottomCenter"
  | "bottomRight";

/** Opening animation hint for the embed runtime. */
export type PopupDisplayAnimation = "smooth" | "fadeIn" | "slideIn" | "none";

export type PopupCloseButtonPosition = "left" | "right";

export interface PopupDesignSettings {
  sizePreset: PopupSizePreset;
  /** Inner padding (px) around popup canvas content in the editor preview. */
  contentPadding: SpacingValue;
  useOverlay: boolean;
  overlayImage: boolean;
  /** Data URL or HTTPS URL when overlay image mode uses custom artwork. */
  overlayImageUrl?: string;
  /**
   * Blur intensity (px) for the backdrop scrim when overlay is on and
   * {@link overlayImage} is off. 0 = sharp, 30 = heavy blur.
   */
  overlayBlur?: number;
  /**
   * @deprecated Use {@link overlayBlur} instead. Kept for backward compat with
   * older saved templates; the editor no longer exposes a tint control.
   */
  overlayOpacity?: number;
  /**
   * Opacity of the overlay background image when {@link overlayImage} is on and URL is set.
   * 0–1.
   */
  overlayImageOpacity?: number;
  /** Toggle: paint a background image on the popup card itself (behind blocks). */
  backgroundImage: boolean;
  /**
   * Data URL or HTTPS URL applied as the popup card's background when
   * {@link backgroundImage} is on. Mirrors `popupConfig.backgroundImage` from
   * the embed runtime in `packages/editor/index.html`.
   */
  backgroundImageUrl?: string;
  /**
   * How the popup card adopts its background image (mirrors `imageFit` in the
   * embed runtime):
   *  - `auto`    — preserve natural aspect ratio (`background-size: auto`)
   *  - `cover`   — fill the card, may crop (default)
   *  - `contain` — fit whole image with letterboxing
   */
  backgroundImageFit?: "auto" | "cover" | "contain";
  /**
   * When true, the popup card is dressed entirely by the image — block content
   * still renders but `image-only` consumers will typically suppress chrome
   * (header / footer). Mirrors `imageOnly` in the embed runtime.
   */
  backgroundImageOnly?: boolean;
  multiColumnEnabled: boolean;
  multiColumnPreset: "two" | "three";
  rtl: boolean;
  /** Where the popup sits relative to the viewport. */
  popupPosition: PopupScreenPosition;
  displayAnimation: PopupDisplayAnimation;
  displayCloseButton: boolean;
  closeButtonPosition: PopupCloseButtonPosition;
  closeOnOverlayClick: boolean;
}

/** Day pattern for a detailed schedule row. */
export type PopupScheduleDayPreset =
  | "everyday"
  | "monday"
  | "tuesday"
  | "wednesday"
  | "thursday"
  | "friday"
  | "saturday"
  | "sunday";

export interface PopupScheduleSlot {
  id: string;
  dayPreset: PopupScheduleDayPreset;
  /** Local time HH:mm (24h). */
  startTime: string;
  /** Local time HH:mm (24h). */
  endTime: string;
}

export interface PopupScheduleSettings {
  /** IANA time zone identifier (see {@link POPUP_SCHEDULE_TIMEZONES}). */
  timeZone: string;
  /** ISO date `yyyy-mm-dd`, or empty = start immediately. */
  startDate: string;
  /** ISO date `yyyy-mm-dd`, or empty = no end date. */
  endDate: string;
  detailedScheduleEnabled: boolean;
  slots: PopupScheduleSlot[];
}

/** How often the campaign may appear again for the same visitor (frequency tab). */
export type PopupShowAgainMode = "page_load" | "session" | "interval";

/** Unit for {@link PopupFrequencyShowAgain.intervalValue} when mode is `interval`. */
export type PopupFrequencyIntervalUnit = "minutes" | "hours" | "days";

export interface PopupFrequencyShowAgain {
  mode: PopupShowAgainMode;
  /** Minimum 1 when {@link mode} is `interval`. */
  intervalValue: number;
  intervalUnit: PopupFrequencyIntervalUnit;
}

/** After the visitor closes the popup — merchandising hint for embed runtimes. */
export type PopupStopShowingAfterClosedBehavior =
  | "never_display_again"
  | "respect_show_again";

export interface PopupFrequencyStopShowing {
  /** Stop permanently after signup / primary CTA click (runtime-defined). */
  afterSignupOrClick: boolean;
  /** Cap impressions per visitor when enabled. */
  afterSeenEnabled: boolean;
  afterSeenCount: number;
  afterClosedEnabled: boolean;
  afterClosedBehavior: PopupStopShowingAfterClosedBehavior;
}

export interface PopupFrequencySettings {
  showAgain: PopupFrequencyShowAgain;
  stopShowing: PopupFrequencyStopShowing;
}

export function createDefaultPopupFrequencySettings(): PopupFrequencySettings {
  return {
    showAgain: {
      mode: "session",
      intervalValue: 7,
      intervalUnit: "days",
    },
    stopShowing: {
      afterSignupOrClick: true,
      afterSeenEnabled: true,
      afterSeenCount: 5,
      afterClosedEnabled: true,
      afterClosedBehavior: "never_display_again",
    },
  };
}

export function normalizePopupFrequencySettings(
  incoming?: Partial<PopupFrequencySettings> | null,
): PopupFrequencySettings {
  const d = createDefaultPopupFrequencySettings();
  if (!incoming) return d;

  const modeRaw = incoming.showAgain?.mode;
  const mode: PopupShowAgainMode =
    modeRaw === "page_load" || modeRaw === "session" || modeRaw === "interval"
      ? modeRaw
      : d.showAgain.mode;

  const intervalValue =
    typeof incoming.showAgain?.intervalValue === "number" &&
    Number.isFinite(incoming.showAgain.intervalValue)
      ? Math.max(1, Math.floor(incoming.showAgain.intervalValue))
      : d.showAgain.intervalValue;

  const unitRaw = incoming.showAgain?.intervalUnit;
  const intervalUnit: PopupFrequencyIntervalUnit =
    unitRaw === "minutes" || unitRaw === "hours" || unitRaw === "days"
      ? unitRaw
      : d.showAgain.intervalUnit;

  const closedRaw = incoming.stopShowing?.afterClosedBehavior;
  const afterClosedBehavior: PopupStopShowingAfterClosedBehavior =
    closedRaw === "respect_show_again" || closedRaw === "never_display_again"
      ? closedRaw
      : d.stopShowing.afterClosedBehavior;

  const afterSeenCount =
    typeof incoming.stopShowing?.afterSeenCount === "number" &&
    Number.isFinite(incoming.stopShowing.afterSeenCount)
      ? Math.max(1, Math.floor(incoming.stopShowing.afterSeenCount))
      : d.stopShowing.afterSeenCount;

  return {
    showAgain: {
      mode,
      intervalValue,
      intervalUnit,
    },
    stopShowing: {
      afterSignupOrClick:
        incoming.stopShowing?.afterSignupOrClick ??
        d.stopShowing.afterSignupOrClick,
      afterSeenEnabled:
        incoming.stopShowing?.afterSeenEnabled ??
        d.stopShowing.afterSeenEnabled,
      afterSeenCount,
      afterClosedEnabled:
        incoming.stopShowing?.afterClosedEnabled ??
        d.stopShowing.afterClosedEnabled,
      afterClosedBehavior,
    },
  };
}

/** Curated zones with labels matching common marketing schedulers (GMT prefix). */
export const POPUP_SCHEDULE_TIMEZONES: readonly {
  id: string;
  label: string;
}[] = [
  { id: "UTC", label: "GMT+0000 (Coordinated Universal Time)" },
  { id: "America/Los_Angeles", label: "GMT−0800 (Pacific Time)" },
  { id: "America/Denver", label: "GMT−0700 (Mountain Time)" },
  { id: "America/Chicago", label: "GMT−0600 (Central Time)" },
  { id: "America/New_York", label: "GMT−0500 (Eastern Time)" },
  { id: "America/Sao_Paulo", label: "GMT−0300 (Brasília Time)" },
  { id: "Europe/London", label: "GMT+0000 (United Kingdom)" },
  { id: "Europe/Paris", label: "GMT+0100 (Central European Time)" },
  { id: "Asia/Dubai", label: "GMT+0400 (Gulf Standard Time)" },
  { id: "Asia/Kolkata", label: "GMT+0530 (India Standard Time)" },
  { id: "Asia/Singapore", label: "GMT+0800 (Singapore)" },
  { id: "Asia/Tokyo", label: "GMT+0900 (Japan Standard Time)" },
  { id: "Australia/Sydney", label: "GMT+1000 / GMT+1100 (Australian Eastern)" },
  { id: "Pacific/Auckland", label: "GMT+1200 / GMT+1300 (New Zealand)" },
];

export interface PopupEmbedSettings {
  triggers: PopupTriggersSettings;
  design: PopupDesignSettings;
  schedule: PopupScheduleSettings;
  /** Campaign frequency / caps — stored under `settings.popup.frequency`. */
  frequency: PopupFrequencySettings;
}

/** Short-key payload for embed / third-party integrations (not the editor storage shape). */
export interface PopupTriggersEmbedApi {
  landing: {
    enabled: boolean;
    triggerConditions: PopupTriggerConditions;
  };
  exit: {
    enabled: boolean;
    triggerConditions: PopupTriggerConditions;
    triggerOnScrollUp: boolean;
    triggerOnBackButton: boolean;
  };
  scroll: {
    enabled: boolean;
    scrollPercentage: number;
    triggerConditions: PopupTriggerConditions;
  };
  click: {
    enabled: boolean;
    elementId: string;
    triggerConditions: PopupTriggerConditions;
  };
  hover: {
    enabled: boolean;
    elementId: string;
    triggerConditions: PopupTriggerConditions;
  };
  custom: {
    enabled: boolean;
    customEventName: string;
    triggerConditions: PopupTriggerConditions;
  };
}

export function createDefaultTriggerConditions(): PopupTriggerConditions {
  return {
    enabled: false,
    delaySeconds: 0,
    pageViews: 0,
    operator: "and",
  };
}

/**
 * Coerces legacy `triggerConditions: true | false` and partial objects into a full object.
 */
export function normalizeTriggerConditions(
  value: PopupTriggerConditions | boolean | undefined | null,
): PopupTriggerConditions {
  if (value === true) {
    return {
      enabled: true,
      delaySeconds: 0,
      pageViews: 0,
      operator: "and",
    };
  }
  if (value === false || value === undefined || value === null) {
    return createDefaultTriggerConditions();
  }
  return {
    enabled: !!value.enabled,
    delaySeconds:
      typeof value.delaySeconds === "number" &&
      Number.isFinite(value.delaySeconds)
        ? Math.max(0, value.delaySeconds)
        : 0,
    pageViews:
      typeof value.pageViews === "number" && Number.isFinite(value.pageViews)
        ? Math.max(0, value.pageViews)
        : 0,
    operator: value.operator === "or" ? "or" : "and",
  };
}

export function normalizePopupTriggerSlot(
  base: PopupTriggerSlot,
  incoming?: Partial<PopupTriggerSlot>,
): PopupTriggerSlot {
  if (incoming === undefined) {
    return {
      ...base,
      triggerConditions: normalizeTriggerConditions(base.triggerConditions),
    };
  }
  const merged: PopupTriggerSlot = { ...base, ...incoming };
  const tcSource = Object.prototype.hasOwnProperty.call(
    incoming,
    "triggerConditions",
  )
    ? incoming.triggerConditions
    : base.triggerConditions;
  merged.triggerConditions = normalizeTriggerConditions(
    tcSource as PopupTriggerConditions | boolean | undefined,
  );
  return merged;
}

export function normalizePopupTriggersSettings(
  base: PopupTriggersSettings,
  incoming?: Partial<PopupTriggersSettings>,
): PopupTriggersSettings {
  const keys = Object.keys(base) as (keyof PopupTriggersSettings)[];
  const out = { ...base };
  for (const k of keys) {
    out[k] = normalizePopupTriggerSlot(base[k], incoming?.[k]);
  }
  return out;
}

/**
 * Maps editor/storage keys (`onLanding`, …) to a compact embed JSON shape (`landing`, …).
 * `click` / `hover` use `elementId` (from `clickAnchor` / `hoverElementId`).
 */
export function toPopupTriggersEmbedJson(
  triggers: PopupTriggersSettings,
): PopupTriggersEmbedApi {
  const tc = (slot: PopupTriggerSlot): PopupTriggerConditions =>
    normalizeTriggerConditions(slot.triggerConditions);

  const clickAnchor = triggers.onClick.clickAnchor ?? "";
  const elementIdFromAnchor = clickAnchor.startsWith("#")
    ? clickAnchor.slice(1)
    : clickAnchor;

  return {
    landing: {
      enabled: triggers.onLanding.enabled,
      triggerConditions: tc(triggers.onLanding),
    },
    exit: {
      enabled: triggers.onExit.enabled,
      triggerConditions: tc(triggers.onExit),
      triggerOnScrollUp: triggers.onExit.triggerOnScrollUp ?? false,
      triggerOnBackButton: triggers.onExit.triggerOnBackButton ?? false,
    },
    scroll: {
      enabled: triggers.onScroll.enabled,
      scrollPercentage: triggers.onScroll.scrollPercent ?? 50,
      triggerConditions: tc(triggers.onScroll),
    },
    click: {
      enabled: triggers.onClick.enabled,
      elementId: elementIdFromAnchor,
      triggerConditions: tc(triggers.onClick),
    },
    hover: {
      enabled: triggers.onHover.enabled,
      elementId: triggers.onHover.hoverElementId ?? "",
      triggerConditions: tc(triggers.onHover),
    },
    custom: {
      enabled: triggers.onCustomEvent.enabled,
      customEventName: triggers.onCustomEvent.customEventName ?? "",
      triggerConditions: tc(triggers.onCustomEvent),
    },
  };
}

/** Pixel widths aligned with common popup presets (content column). */
export const POPUP_SIZE_PRESET_WIDTH: Record<PopupSizePreset, number> = {
  small: 380,
  medium: 480,
  large: 600,
  fullscreen: 960,
  fullHeight: 420,
  custom: 600,
};

function defaultScheduleSlots(): PopupScheduleSlot[] {
  return [
    {
      id: "schedule-slot-1",
      dayPreset: "everyday",
      startTime: "00:00",
      endTime: "23:59",
    },
    {
      id: "schedule-slot-2",
      dayPreset: "everyday",
      startTime: "00:00",
      endTime: "23:59",
    },
  ];
}

export function createDefaultPopupEmbedSettings(): PopupEmbedSettings {
  const off = createDefaultTriggerConditions();
  return {
    triggers: {
      onLanding: { enabled: true, triggerConditions: { ...off } },
      onExit: {
        enabled: false,
        triggerConditions: { ...off },
        triggerOnScrollUp: false,
        triggerOnBackButton: false,
      },
      onScroll: {
        enabled: false,
        scrollPercent: 50,
        triggerConditions: { ...off },
      },
      onClick: {
        enabled: false,
        clickAnchor: "#popup",
        triggerConditions: { ...off },
      },
      onHover: {
        enabled: false,
        hoverElementId: "",
        triggerConditions: { ...off },
      },
      onCustomEvent: {
        enabled: false,
        customEventName: "",
        triggerConditions: { ...off },
      },
    },
    design: {
      sizePreset: "large",
      contentPadding: { top: 24, right: 24, bottom: 24, left: 24 },
      useOverlay: true,
      overlayImage: false,
      overlayBlur: 8,
      overlayImageOpacity: 1,
      backgroundImage: false,
      backgroundImageUrl: "",
      backgroundImageFit: "cover",
      backgroundImageOnly: false,
      multiColumnEnabled: true,
      multiColumnPreset: "two",
      rtl: false,
      popupPosition: "center",
      displayAnimation: "smooth",
      displayCloseButton: true,
      closeButtonPosition: "right",
      closeOnOverlayClick: true,
    },
    schedule: {
      timeZone: "Asia/Kolkata",
      startDate: "",
      endDate: "",
      detailedScheduleEnabled: true,
      slots: defaultScheduleSlots(),
    },
    frequency: createDefaultPopupFrequencySettings(),
  };
}
